// Закреплённые представления: чтение и правка.
//
// Ключ один на весь список — его читают и боковое меню, и дашборд, и страница
// представления. После любой правки список сбрасывается целиком: закрепление
// меняет и меню, и дашборд сразу, и частичная инвалидация здесь только
// разъезжалась бы.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';

import {
  createSavedView,
  deleteSavedView,
  fetchSavedViewData,
  listSavedViews,
  updateSavedView,
  type SaveViewPayload,
  type SavedView,
} from '../api/views.client.js';
import type { ViewResult } from '../../../views/result.js';

export const SAVED_VIEWS_KEY = ['views', 'saved'] as const;

export function useSavedViews(): UseQueryResult<SavedView[]> {
  return useQuery({ queryKey: SAVED_VIEWS_KEY, queryFn: listSavedViews });
}

export function useSavedViewData(id: string | undefined): UseQueryResult<ViewResult> {
  return useQuery({
    queryKey: [...SAVED_VIEWS_KEY, id, 'data'],
    queryFn: () => fetchSavedViewData(id as string),
    enabled: id !== undefined,
  });
}

export function useCreateSavedView(): UseMutationResult<SavedView, Error, SaveViewPayload> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createSavedView,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: SAVED_VIEWS_KEY }),
  });
}

export function useUpdateSavedView(): UseMutationResult<
  SavedView,
  Error,
  { id: string; payload: Partial<SaveViewPayload> }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }) => updateSavedView(id, payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: SAVED_VIEWS_KEY }),
  });
}

export function useDeleteSavedView(): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteSavedView,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: SAVED_VIEWS_KEY }),
  });
}
